/**
 * Sprint TF-1 Phase 1 — Client TalentFix page.
 *
 * Replaces the previously-synthetic ClientTalentFix demo page with the real
 * Stage-1 surface. Client Admins can:
 *   - Open a case (signal-driven, manual, or preventive advisory)
 *   - See the list of cases scoped to their company
 *   - View confidentiality-aware case detail
 *
 * Stages 2-7 are NOT exposed here (TF-2+).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { SuiteHero } from '@/components/client/SuiteHero';
import { GuidedEmptyState } from '@/components/client/GuidedEmptyState';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Loader2, Wrench, Plus, FileSearch, Inbox, Calendar, User, Shield, ShieldCheck, FileEdit } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { DonutCard, ProgressCard, SEM } from '@/components/client/SuiteAnalytics';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import OpenCaseDialog from '@/components/talentfix/OpenCaseDialog';
import { CaseStatusBadge, CaseScopeBadge, CaseOriginPill } from '@/components/talentfix/CaseBadges';
import { getUiLocale } from '@/lib/dates';

function CaseCard({ c, onClick }) {
  const { t } = useTranslation();
  const created = c.created_at ? new Date(c.created_at).toLocaleDateString(getUiLocale()) : '—';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tf-case-row-${c.id}`}
      className="w-full text-left p-4 rounded-lg border border-border/70 bg-card hover:border-primary/50 hover:shadow-sm transition-smooth"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <CaseOriginPill origin={c.origin_type} />
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t(`talentFix.scope.${c.scope_unit}`)}
            </span>
          </div>
          <p className="font-semibold truncate">{c.title || '—'}</p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{created}</span>
            {c.assigned_advisor_name && (
              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{c.assigned_advisor_name}</span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <CaseStatusBadge status={c.status} />
          <CaseScopeBadge scope={c.confidentiality_scope} />
        </div>
      </div>
    </button>
  );
}

function CaseDetailPanel({ caseObj }) {
  const { t } = useTranslation();
  if (!caseObj) return null;
  const requested = caseObj.created_at ? new Date(caseObj.created_at).toLocaleString(getUiLocale()) : '—';
  const isPreventive = caseObj.origin_type === 'preventive_advisory';
  const decided = caseObj.acknowledged_at ? new Date(caseObj.acknowledged_at).toLocaleString(getUiLocale()) : null;
  return (
    <div className="mt-4" data-testid={`tf-case-detail-${caseObj.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CaseOriginPill origin={caseObj.origin_type} />
          <h3 className="text-lg font-semibold mt-1" data-testid="tf-detail-title">{caseObj.title}</h3>
          <p className="text-xs text-muted-foreground mt-1">{t(`talentFix.scope.${caseObj.scope_unit}`)}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <CaseStatusBadge status={caseObj.status} />
          <CaseScopeBadge scope={caseObj.confidentiality_scope} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldCreated')}</p>
          <p className="font-medium" data-testid="tf-detail-created">{requested}</p>
        </div>
        {decided && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {caseObj.status === 'accepted' ? t('talentFix.stage.stage2_assessment') : t('talentFix.status.declined')}
            </p>
            <p className="font-medium" data-testid="tf-detail-acknowledged">{decided}</p>
          </div>
        )}
        {caseObj.assigned_advisor_name && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldAdvisor')}</p>
            <p className="font-medium">{caseObj.assigned_advisor_name}</p>
          </div>
        )}
        {isPreventive && caseObj.target_horizon_months && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldHorizon')}</p>
            <p className="font-medium">{t('talentFix.monthsShort', { n: caseObj.target_horizon_months })}</p>
          </div>
        )}
        {isPreventive && caseObj.executive_sponsor_name && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldSponsor')}</p>
            <p className="font-medium">{caseObj.executive_sponsor_name}</p>
          </div>
        )}
      </div>

      {caseObj.description && (
        <div className="mt-4 p-3 rounded-md bg-muted/40 text-sm" data-testid="tf-detail-description">
          {caseObj.description}
        </div>
      )}

      {caseObj.status === 'declined' && caseObj.decline_reason && (
        <div className="mt-3 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm" data-testid="tf-detail-decline-reason">
          <p className="text-xs uppercase tracking-wider text-destructive font-medium">{t('talentFix.declineReason')}</p>
          <p className="mt-1">{caseObj.decline_reason}</p>
          {caseObj.decline_redirect_to && (
            <p className="text-xs text-muted-foreground mt-1">→ {caseObj.decline_redirect_to}</p>
          )}
        </div>
      )}

      {/* Phase 2 — Client Admin can review the diagnostic plan & consent */}
      {(caseObj.status === 'accepted' || caseObj.status === 'in_progress') && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/client/talentfix/cases/${caseObj.id}/consent`}>
            <Button size="sm" data-testid={`tf-review-consent-btn-${caseObj.id}`}>
              <ShieldCheck className="mr-1.5 h-4 w-4" />{t('talentFix.reviewPlan', { defaultValue: 'Review diagnostic plan' })}
            </Button>
          </Link>
          <Link to={`/client/talentfix/cases/${caseObj.id}/context`}>
            <Button size="sm" variant="outline" data-testid={`tf-context-btn-${caseObj.id}`}>
              <FileEdit className="mr-1.5 h-4 w-4" />{t('talentFix.submitContext', { defaultValue: 'Submit case context' })}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function ClientTalentFix() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clientApi.talentFixListCases({});
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'closed') return items.filter((c) => ['closed', 'declined'].includes(c.status));
    // Founder UAT TF-002 — "Accepted" must match the KPI (accepted + in_progress).
    if (filter === 'accepted') return items.filter((c) => ['accepted', 'in_progress'].includes(c.status));
    return items.filter((c) => c.status === filter);
  }, [items, filter]);

  const summary = useMemo(() => ({
    pending: items.filter((c) => c.status === 'pending').length,
    accepted: items.filter((c) => c.status === 'accepted' || c.status === 'in_progress').length,
    closed: items.filter((c) => c.status === 'closed' || c.status === 'declined').length,
  }), [items]);

  const submit = useCallback(async (body) => {
    const newCase = await clientApi.talentFixOpenCase(body);
    await load();
    setSelected(newCase);
  }, [load]);

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="client-talentfix-page">
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('talentFix.title')}
            <Badge className="bg-primary/15 text-primary border-primary/30">{t('suiteChip.talentfix')}</Badge>
          </span>
        )}
        subtitle={t('talentFix.subtitleClient')}
        actions={(
          <Button size="sm" onClick={() => setOpenDialog(true)} data-testid="tf-new-case-btn" className="gap-1.5">
            <Plus className="h-4 w-4" />
            {t('talentFix.newCase')}
          </Button>
        )}
      />

      <SuiteHero suite="talentfix" icon={Wrench} />

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tf-summary">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.status.pending')}</p>
          <p className="text-2xl font-bold tabular-nums">{summary.pending}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.status.accepted')}</p>
          <p className="text-2xl font-bold tabular-nums">{summary.accepted}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.status.closed')}</p>
          <p className="text-2xl font-bold tabular-nums">{summary.closed}</p>
        </Card>
      </div>

      {/* Founder UAT 2026-06 — executive visual analytics (real data only) */}
      {items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tf-analytics-row">
          <DonutCard
            title={t('talentFix.analytics.statusTitle', { defaultValue: 'Casos por estado' })}
            testid="tf-chart-status"
            accent={SEM.orange}
            data={[
              { name: t('talentFix.status.pending'), value: summary.pending, color: SEM.yellow },
              { name: t('talentFix.status.accepted'), value: summary.accepted, color: SEM.orange },
              { name: t('talentFix.status.closed'), value: summary.closed, color: SEM.green },
            ]}
            emptyText={t('talentFix.analytics.empty', { defaultValue: 'Sin casos registrados todavía.' })}
          />
          <ProgressCard
            title={t('talentFix.analytics.pipelineTitle', { defaultValue: 'Progreso del pipeline correctivo' })}
            testid="tf-chart-pipeline"
            accent={SEM.red}
            rows={[
              { label: t('talentFix.status.pending'), value: summary.pending, color: SEM.yellow },
              { label: t('talentFix.status.accepted'), value: summary.accepted, color: SEM.orange },
              { label: t('talentFix.status.closed'), value: summary.closed, color: SEM.green },
            ]}
            emptyText={t('talentFix.analytics.empty', { defaultValue: 'Sin casos registrados todavía.' })}
          />
        </div>
      )}

      {/* List */}
      <Card className="p-5 shadow-card">
        <Tabs value={filter} onValueChange={setFilter} data-testid="tf-filter-tabs">
          <TabsList>
            <TabsTrigger value="all" data-testid="tf-filter-all">{t('talentFix.filterAll')} ({items.length})</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tf-filter-pending">{t('talentFix.filterPending')}</TabsTrigger>
            <TabsTrigger value="accepted" data-testid="tf-filter-accepted">{t('talentFix.filterAccepted')}</TabsTrigger>
            <TabsTrigger value="closed" data-testid="tf-filter-closed">{t('talentFix.filterClosed')}</TabsTrigger>
          </TabsList>
          <TabsContent value={filter} className="mt-3">
            {loading ? (
              <div className="py-10 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
              </div>
            ) : filtered.length === 0 ? (
              <GuidedEmptyState
                dataTestId="tf-empty"
                icon={Inbox}
                title={t('guidedEmpty.talentfix.title')}
                why={t('guidedEmpty.talentfix.why')}
                steps={[1, 2, 3, 4].map((n) => t(`guidedEmpty.talentfix.step${n}`))}
                cta={(
                  <Button size="sm" onClick={() => setOpenDialog(true)} data-testid="tf-empty-open-case-btn" className="gap-1.5">
                    <Plus className="h-4 w-4" /> {t('talentFix.newCase')}
                  </Button>
                )}
              />
            ) : (
              <ul className="space-y-2" data-testid="tf-case-list">
                {filtered.map((c) => (
                  <li key={c.id}>
                    <CaseCard c={c} onClick={() => setSelected(c)} />
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Founder UAT TF-001 — case detail lives in a clearly-labelled drawer
          instead of an inline card that looked like a duplicate of the list row. */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="tf-case-detail-sheet">
          <SheetHeader>
            <SheetTitle>{t('talentFix.detailTitle', { defaultValue: 'Case detail' })}</SheetTitle>
            <SheetDescription>{t('talentFix.detailSubtitle', { defaultValue: 'Full information for the selected case.' })}</SheetDescription>
          </SheetHeader>
          {selected && <CaseDetailPanel caseObj={selected} />}
        </SheetContent>
      </Sheet>

      <OpenCaseDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onSubmit={submit}
        isAdmin={false}
      />
    </div>
  );
}
